'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { MagnifyingGlass, Lightbulb, Flask, Rocket, ChartBar, Brain, Stack, Database } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function Process() {
  return (
    <div className="min-h-screen relative overflow-hidden transition-none">
      
      <Components.GlassNavigation />
      
      <main className="pt-16 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--brutal-ink)] mb-6 leading-tight">
              My Process
            </h1>
            <p className="text-lg md:text-xl text-[var(--brutal-muted)] max-w-4xl mx-auto leading-relaxed mb-8">
                      "They say design is a team sport. I believe design should feel more like a cooperative board game where everyone wants to help each other get to the top of the mountain together."
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="space-y-8">
              {[
                {
                  icon: MagnifyingGlass,
                  title: 'Discovery & Research',
                  body: 'Start with user needs, business goals, and technical constraints. Deep-research AI helps with early synthesis and competitive scans. Interviews, usability tests, and stakeholder work confirm what is true before findings shape the plan.',
                  chip: 'bg-[#7eb8da]',
                },
                {
                  icon: Lightbulb,
                  title: 'Ideation & Strategy',
                  body: 'Transform insights into actionable design strategies through collaborative workshops, journey mapping, and information architecture planning.',
                  chip: 'bg-[var(--brutal-mustard)]',
                },
                {
                  icon: Flask,
                  title: 'Prototyping & Testing',
                  body: 'Rapid prototyping and iterative testing to validate decisions before development. Build harnesses, patterns, and shared structures so the team can move fast and keep design quality and consistency steady when AI makes it easy to produce a lot of work.',
                  chip: 'bg-[#8fce8f]',
                },
                {
                  icon: Rocket,
                  title: 'Implementation & Launch',
                  body: 'Creating systems that ensure pixel-perfect implementation is simple and straightforward, with invisible handoffs & tight design system governance.',
                  chip: 'bg-[var(--brutal-accent)]',
                },
                {
                  icon: ChartBar,
                  title: 'Measurement & Optimization',
                  body: 'Post-launch analytics and user feedback to improve the product. Build a clear database structure and platform so research data and AI-supported insights are easy to measure, compare, and use in decisions.',
                  chip: 'bg-[#c4a7e7]',
                },
              ].map(({ icon: Icon, title, body, chip }) => (
                <div key={title} className="flex items-start space-x-6 group">
                  <div className={`w-10 h-10 ${chip} border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center flex-shrink-0 group-hover:-translate-y-0.5 group-hover:shadow-[var(--brutal-shadow)] transition-all duration-150`}>
                    <Icon weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-3">{title}</h3>
                    <p className="text-[var(--brutal-muted)] leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
              <Card className="brutal-panel p-6">
                <h3 className="text-xl font-bold text-[var(--brutal-ink)] mb-4">Key Principles</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Human-Centered</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Every decision starts with understanding user needs and behaviors</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Data-Driven</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Validate assumptions with research and measure impact with metrics</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Business Value Balance</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Balancing business value and operations with a human-centered data-informed approach</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Outcome-Driven Planning</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Develop a vision, understand the steps to get there, make a plan, and execute quickly</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Cooperative</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Like a board game where everyone works together toward shared success</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-panel)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">Iterative</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Continuous improvement through testing and feedback loops</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-mustard)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="text-[var(--brutal-ink)] font-semibold">AI with judgment</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">AI for speed and breadth. Taste, research rigor, and the final design call stay with people.</p>
                    </div>
                  </div>
                </div>
              </Card>
          </div>

          <section className="mb-16" aria-labelledby="ai-craft-heading">
            <h2 id="ai-craft-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-6 text-center">
              How AI fits the process
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="brutal-panel p-6">
                <div className="w-10 h-10 bg-[#7eb8da] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center mb-4">
                  <Brain weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                </div>
                <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Collaborative Research</h3>
                <p className="text-[var(--brutal-muted)] text-sm leading-relaxed">
                  Deep-research AI helps on the first pass: competitors, themes, open questions. Interviews and user tests decide what we believe. New tools sit beside traditional research in the same discovery loop.
                </p>
              </Card>
              <Card className="brutal-panel p-6">
                <div className="w-10 h-10 bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center mb-4">
                  <Stack weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                </div>
                <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Harness your hopes</h3>
                <p className="text-[var(--brutal-muted)] text-sm leading-relaxed">
                  Part of the process is building design systems, prototypes, and implementation harnesses. Those structures make rapid prototyping possible and keep quality consistent when AI makes it easy to generate a lot of work quickly.
                </p>
              </Card>
              <Card className="brutal-panel p-6">
                <div className="w-10 h-10 bg-[#8fce8f] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] rounded-[10px] flex items-center justify-center mb-4">
                  <Database weight="duotone" className="w-5 h-5 text-[var(--brutal-ink)]" />
                </div>
                <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-2">Evidence you can measure</h3>
                <p className="text-[var(--brutal-muted)] text-sm leading-relaxed">
                  Develop a database structure and platform where research data and AI-supported insights live together, so the team can measure what was learned and put it into product decisions.
                </p>
              </Card>
            </div>
          </section>

          <Card className="brutal-panel p-8 text-center">
            <h3 className="text-2xl font-bold text-[var(--brutal-ink)] mb-4">Ready to Transform Your Product?</h3>
            <p className="text-[var(--brutal-muted)] mb-6 max-w-2xl mx-auto">
              Let's apply this proven methodology to solve your most complex design challenges and drive measurable business results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                variant="outline" 
                className="bg-[var(--brutal-panel)] border-[var(--brutal-ink)] text-[var(--brutal-ink)] hover:bg-[var(--brutal-kraft)] hover:text-[var(--brutal-ink)]"
              >
                <Link to="/">View Case Studies</Link>
              </Button>
              <Button 
                asChild 
                className="brutal-btn text-[var(--brutal-ink)] border-0"
              >
                <Link to="/Contact">Get In Touch</Link>
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  )
}